// The town the party starts every run in.
//
// It is laid out as streets rather than rooms: blocks of houses divided by
// streets, with a plaza where the middle streets cross. Each trade keeps a
// room inside one of those blocks, entered through a door with its own front
// painted on the wall either side of it. What the party actually uses is the
// counter in the room: an altar, a lectern, a shop counter, a bar.
//
// The ground is laid as well as built: a green on the plaza, dirt in the back
// lanes, and each trade floored to suit — marble in the temple, rugs in the
// school and the tavern, tiles in the market, flags where people train.
//
// Nothing lives here. There are no monsters, no chests and no locked doors,
// only the five trades and the gate down into the dungeon.

// The trades, in the order they are given rooms: the four around the plaza
// first, then the tavern.
export const PLAZA_TRADES = ['temple', 'magic', 'market', 'training'];
export const FACILITIES = [...PLAZA_TRADES, 'tavern'];

// What each trade's counter is, as a square on the map. The dungeon's own
// table (see dungeon.js) is the same thing keyed the other way round; it is
// repeated here so the generator does not have to reach into the dungeon.
const COUNTER_TILES = { training: 9, magic: 12, market: 13, temple: 14, tavern: 15 };

// What each trade's room is floored with
const SHOP_FLOORS = {
    temple: 'floor_temple',
    magic: 'carpet',
    market: 'floor_tile',
    training: 'floor_stone',
    tavern: 'carpet',
};

// The ways down out of a town. Which one a town has is its own business, so
// it is drawn from the seed along with its name.
export const ENTRANCE_STYLES = ['stairs', 'sewer', 'tower', 'castle'];

export function townEntrance(rng) {
    return ENTRANCE_STYLES[Math.floor(rng() * ENTRANCE_STYLES.length)];
}

// Big enough for every trade to have a building of its own, with a room
// inside it worth walking into, and still a third of a dungeon level
export const TOWN_SIZE = 22;

const MARGIN = 1;        // the town wall, before the outermost street
const STREET = 2;        // streets are two squares wide
const BLOCK = 4;         // and a block of houses is four, so the room
const ROOM = 2;          // inside it is two squares across

// Names are built from the run's seed, so a seed always returns to the same town
const FIRST = ['Green', 'Stone', 'Oak', 'White', 'Iron', 'Raven', 'Wind', 'Salt', 'Thorn', 'Amber'];
const SECOND = ['haven', 'ford', 'gate', 'reach', 'hollow', 'barrow', 'market', 'watch', 'fell', 'crest'];

export function townName(rng) {
    return FIRST[Math.floor(rng() * FIRST.length)] + SECOND[Math.floor(rng() * SECOND.length)];
}

const WALL = 1;
const FLOOR = 0;
const DOOR_ACROSS = 3;   // a door crossed going east or west
const DOOR_ALONG = 5;    // ...and one crossed going north or south

function carve(tiles, x0, y0, x1, y1) {
    for (let y = Math.max(0, y0); y <= Math.min(tiles.length - 1, y1); y++) {
        for (let x = Math.max(0, x0); x <= Math.min(tiles[0].length - 1, x1); x++) {
            tiles[y][x] = FLOOR;
        }
    }
}

const at = (tiles, [x, y]) => (tiles[y] ? tiles[y][x] : undefined);
const DIRECTIONS = [[0, -1], [1, 0], [0, 1], [-1, 0]];

// The smallest map the street grid fits in: the town wall, two blocks of
// houses, and the three streets that frame them. Anything smaller is built as
// a single block with streets all round it instead (see oneBlockTown).
export const GRID_MINIMUM = MARGIN * 2 + 2 * BLOCK + 3 * STREET;

// Where the streets run. As many blocks as will fit, and whatever is left over
// goes to the town wall, so the grid sits square in the middle of the map.
export function streetLines(size) {
    const inside = size - MARGIN * 2;
    const blocks = Math.max(2, Math.floor((inside - STREET) / (BLOCK + STREET)));
    const used = blocks * BLOCK + (blocks + 1) * STREET;
    const first = MARGIN + Math.floor((inside - used) / 2);
    return Array.from({ length: blocks + 1 }, (unused, i) => first + i * (BLOCK + STREET));
}

// The bands of houses between the streets, as [from, to] along one axis
function blockBands(lines) {
    const bands = [];
    for (let i = 0; i < lines.length - 1; i++) {
        bands.push([lines[i] + STREET, lines[i + 1] - 1]);
    }
    return bands.filter(([from, to]) => to >= from);
}

// Open a shop inside a block of houses: a room big enough to walk about in,
// a counter in the far corner of it, a door onto the street, and the trade's
// front painted over the wall that door is cut into.
//
// `block` is the block's top-left corner and `towards` the way the street
// lies from it. The room is the inside of the block, a square short of its
// walls on every side.
function openShop(town, block, towards, trade) {
    const { tiles, surrounds, doors } = town;
    const [bx, by] = block;
    const [dx, dy] = towards;
    const room = { x0: bx + 1, y0: by + 1, x1: bx + ROOM, y1: by + ROOM };

    // The door is cut through the middle of the wall the street is on
    const door = [
        dx === 0 ? room.x0 : (dx < 0 ? room.x0 - 1 : room.x1 + 1),
        dy === 0 ? room.y0 : (dy < 0 ? room.y0 - 1 : room.y1 + 1),
    ];
    const outside = [door[0] + dx, door[1] + dy];

    // Every square of the room has to be solid block to start with, and there
    // has to be a street on the far side of the door to come in from
    for (let y = room.y0; y <= room.y1; y++) {
        for (let x = room.x0; x <= room.x1; x++) {
            if (at(tiles, [x, y]) !== WALL) return false;
        }
    }
    if (at(tiles, door) !== WALL || at(tiles, outside) !== FLOOR) return false;

    carve(tiles, room.x0, room.y0, room.x1, room.y1);
    for (let y = room.y0; y <= room.y1; y++) {
        for (let x = room.x0; x <= room.x1; x++) town.floors[y][x] = SHOP_FLOORS[trade];
    }

    // The counter stands in the corner furthest from the door, so there is
    // room to come in and walk round it
    const counter = [
        dx === 0 ? room.x1 : (dx < 0 ? room.x1 : room.x0),
        dy === 0 ? room.y1 : (dy < 0 ? room.y1 : room.y0),
    ];
    tiles[counter[1]][counter[0]] = COUNTER_TILES[trade];

    // A door across the way out is crossed east-west; one along it, north-south
    tiles[door[1]][door[0]] = dx !== 0 ? DOOR_ACROSS : DOOR_ALONG;
    doors.push({ x: door[0], y: door[1], orientation: dx !== 0 ? 'horizontal' : 'vertical' });
    // ...and the front is painted over that same stretch of wall, around the door
    surrounds[door[1]][door[0]] = trade;
    return { counter };
}

// Nowhere indoors to put this trade: stand its counter in the street with the
// building front painted on the wall behind it, like a stall against a shop.
// A stall needs that wall, so an open square in the middle of the street is
// no good to it.
function openStall(town, square, trade) {
    const { tiles, surrounds } = town;
    const [x, y] = square;
    if (at(tiles, square) !== FLOOR) return false;
    const wall = DIRECTIONS.find(([dx, dy]) => at(tiles, [x + dx, y + dy]) === WALL);
    if (!wall) return false;
    tiles[y][x] = COUNTER_TILES[trade];
    surrounds[y + wall[1]][x + wall[0]] = trade;
    return true;
}

// The nearest square to stand a stall on: open, against a building, and not
// already somebody else's pitch
function stallSpot(tiles, from) {
    const seen = new Set([from.join(',')]);
    const queue = [from];
    while (queue.length) {
        const square = queue.shift();
        const [x, y] = square;
        if (at(tiles, square) === FLOOR && DIRECTIONS.some(([dx, dy]) => at(tiles, [x + dx, y + dy]) === WALL)) {
            return square;
        }
        DIRECTIONS.forEach(([dx, dy]) => {
            const next = [x + dx, y + dy];
            if (seen.has(next.join(',')) || at(tiles, next) === undefined) return;
            seen.add(next.join(','));
            if (at(tiles, next) === FLOOR) queue.push(next);
        });
    }
    return null;
}

function emptyTown(size) {
    return {
        tiles: Array.from({ length: size }, () => new Array(size).fill(WALL)),
        surrounds: Array.from({ length: size }, () => new Array(size).fill(null)),
        floors: Array.from({ length: size }, () => new Array(size).fill(null)),
        doors: [],
    };
}

// Lay ground over a stretch of the town, leaving the buildings alone
function lay(town, floor, x0, y0, x1, y1) {
    for (let y = Math.max(0, y0); y <= Math.min(town.tiles.length - 1, y1); y++) {
        for (let x = Math.max(0, x0); x <= Math.min(town.tiles.length - 1, x1); x++) {
            if (town.tiles[y][x] !== WALL) town.floors[y][x] = floor;
        }
    }
}

// A town too small for a plaza: one block of houses in the middle, with the
// streets running all the way round it. Its trades keep stalls in the street,
// since there is no room inside for five shops.
function oneBlockTown(size) {
    const town = emptyTown(size);
    const first = MARGIN;
    const last = size - MARGIN - 1;
    carve(town.tiles, first, first, last, last);

    const from = first + STREET;
    const to = last - STREET;
    for (let y = from; y <= to; y++) {
        for (let x = from; x <= to; x++) town.tiles[y][x] = WALL;
    }
    const middle = Math.round((from + to) / 2);

    // Cobbles round the block, bare earth in the lanes against the town wall
    lay(town, 'floor_dirt', first, first, last, last);
    lay(town, 'floor_cobble', first + 1, first + 1, last - 1, last - 1);

    const faces = [[middle, from - 1], [to + 1, middle], [middle, to + 1], [from - 1, middle]];
    const facilities = {};
    PLAZA_TRADES.forEach((trade, index) => {
        if (openStall(town, faces[index], trade)) facilities[trade] = faces[index];
    });
    if (openStall(town, [first, first], 'tavern')) facilities.tavern = [first, first];

    return {
        tiles: town.tiles,
        surrounds: town.surrounds,
        floors: town.floors,
        doors: town.doors,
        facilities,
        start_position: [middle, last],
        gate: [middle, first],
    };
}

// The whole town: streets, plaza, the trades in their buildings, and the gate
export function buildTown(size = TOWN_SIZE, rng = Math.random) {
    const entranceStyle = townEntrance(rng);
    if (size < GRID_MINIMUM) return { ...oneBlockTown(size), entranceStyle };
    const town = emptyTown(size);
    const { tiles } = town;
    const lines = streetLines(size);

    // Streets both ways, which leaves the blocks of houses standing between them
    const from = lines[0];
    const to = lines[lines.length - 1] + STREET - 1;
    lines.forEach((line) => {
        carve(tiles, line, from, line + STREET - 1, to);
        carve(tiles, from, line, to, line + STREET - 1);
    });

    // A plaza where the middle streets cross, for the town to gather in
    const middle = lines[Math.floor(lines.length / 2)];
    carve(tiles, middle - 1, middle - 1, middle + STREET, middle + STREET);

    // Bare earth in the lanes along the town wall, cobbles the length of every
    // street inside them, and a green on the plaza with cobbles round its edge
    lay(town, 'floor_dirt', from, from, to, to);
    lines.slice(1, -1).forEach((line) => {
        lay(town, 'floor_cobble', line, from, line + STREET - 1, to);
        lay(town, 'floor_cobble', from, line, to, line + STREET - 1);
    });
    lay(town, 'floor_cobble', middle - 1, middle - 1, middle + STREET, middle + STREET);
    lay(town, 'floor_grass', middle, middle, middle + STREET - 1, middle + STREET - 1);

    // The blocks either side of the plaza, and the room in the middle of each
    const bands = blockBands(lines);
    const before = bands.filter(([, end]) => end < middle).pop();
    const after = bands.find(([start]) => start > middle + STREET - 1);

    // Each of the four blocks around the plaza, with the way the plaza lies
    const buildings = [];
    if (before && after) {
        buildings.push(
            { block: [before[0], before[0]], towards: [1, 0] },      // north-west
            { block: [after[0], before[0]], towards: [0, 1] },       // north-east
            { block: [after[0], after[0]], towards: [-1, 0] },       // south-east
            { block: [before[0], after[0]], towards: [0, -1] },      // south-west
        );
        // ...and one more block for the tavern, out towards the town wall
        const edge = bands.find((band) => band !== before && band !== after);
        if (edge) buildings.push({ block: [edge[0], after[0]], towards: [0, 1] });
    }

    const facilities = {};
    FACILITIES.forEach((trade, index) => {
        const building = buildings[index];
        if (!building) return;
        // Every side of the block is tried, in case the plaza ate the first
        let opened = null;
        [building.towards, ...DIRECTIONS].some((towards) => {
            opened = openShop(town, building.block, towards, trade);
            return !!opened;
        });
        if (opened) facilities[trade] = opened.counter;
    });

    // The party arrives on the street at the near side of town, and the gate
    // down into the dungeon is set into the town wall at the far side
    const street = middle + STREET - 1;
    const start = [street, lines[lines.length - 1]];
    const gate = [street, lines[0]];

    // Any trade that found no building keeps a stall in the street instead,
    // against the nearest wall to where the party comes in
    FACILITIES.filter((trade) => !facilities[trade]).forEach((trade) => {
        const spot = stallSpot(tiles, start);
        if (spot && openStall(town, spot, trade)) facilities[trade] = spot;
    });

    return {
        tiles,
        surrounds: town.surrounds,
        floors: town.floors,
        doors: town.doors,
        facilities,
        start_position: start,
        gate,
        entranceStyle,
    };
}
